import {useCallback, useState} from 'react'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useClickAnalytics} from '@github-ui/use-analytics'
import type {CopilotFixDialogSubmitOptions} from './CopilotFixDialog'

interface UseFixWithCopilotOptions {
  basePath: string
}

export interface SubmitFixOptions extends CopilotFixDialogSubmitOptions {
  reviewReferences: string[]
  fixAll: boolean
  /** Additional properties merged into the analytics event. */
  analyticsExtras?: Record<string, unknown>
}

/**
 * Encapsulates the shared submission logic for the "Fix with Copilot" flow:
 * POST to `code_review_fix_with_copilot`, error handling, analytics, and
 * task URL extraction.
 */
export function useFixWithCopilot({basePath}: UseFixWithCopilotOptions) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const [taskUrl, setTaskUrl] = useState<string | null>(null)

  const submitFix = useCallback(
    async ({createPullRequest, instructions, model, reviewReferences, fixAll, analyticsExtras}: SubmitFixOptions) => {
      const response = await verifiedFetchJSON(`${basePath}/code_review_fix_with_copilot`, {
        method: 'POST',
        body: {
          create_pull_request: createPullRequest,
          fix_all: fixAll,
          review_references: reviewReferences,
          ...(model ? {model} : {}),
          ...(instructions ? {additional_instructions: instructions} : {}),
        },
      })

      if (!response.ok) {
        let data = null
        try {
          data = await response.json()
        } catch {
          // ignore JSON parse errors
        }
        throw new Error(data?.error || data?.message || `Failed to Fix with Copilot (${response.status})`)
      }

      const data = await response.json()

      sendClickAnalyticsEvent({
        ...analyticsExtras,
        category: 'ccr_fix_with_copilot',
        action: 'submit',
        applyMode: createPullRequest ? 'pull_request' : 'commit',
      })

      if (!data?.task_url) {
        throw new Error('No task URL returned')
      }
      setTaskUrl(data.task_url)
    },
    [basePath, sendClickAnalyticsEvent],
  )

  return {taskUrl, submitFix}
}
